import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { withLocalePrefix } from '../../../shared/utils/translation';
import { consumer } from './cable';
import { standardRequestWithErrorFlash } from '../../../shared/utils/request';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

export type ChatSummary = {
  id: number;
  title: string | null;
  updated_at: string;
};

type ServerMessage = {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
};

type ChatDetail = {
  id: number;
  title: string | null;
  updated_at: string;
  messages: ServerMessage[];
  has_more: boolean;
  next_before_id: number | null;
};

type MessagePageResponse = {
  messages: ServerMessage[];
  has_more: boolean;
  next_before_id: number | null;
};

type AgentChatContextValue = {
  chats: ChatSummary[];
  activeChatId: number | null;
  loadingChat: boolean;
  messages: ChatMessage[];
  streamingContent: string;
  isStreaming: boolean;
  suggestedReplies: string[];
  error: string | null;
  hasMoreMessages: boolean;
  loadingEarlier: boolean;
  sendMessage: (text: string) => Promise<void>;
  loadChat: (id: number) => Promise<void>;
  loadEarlierMessages: () => Promise<void>;
  startNewChat: () => void;
  refreshChats: (limit?: number) => Promise<void>;
  renameChat: (id: number, title: string) => Promise<boolean>;
  deleteChat: (id: number) => Promise<boolean>;
};

const STREAM_TIMEOUT_MS = 30000;
const t = withLocalePrefix('candidate_profile_redesign.agent_chat');

type StreamEvent = {
  type: string;
  content?: string;
  full_content?: string;
  message_id?: number;
  message?: string;
  title?: string;
  suggestions?: string[];
  seq?: number;
};

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

type AgentChatProviderProps = {
  applicationId: number;
  children: ReactNode;
};

const toClientMessage = (m: ServerMessage): ChatMessage => ({
  id: `${m.role}-${m.id}`,
  role: m.role,
  content: m.content,
});

export const AgentChatProvider = ({
  applicationId,
  children,
}: AgentChatProviderProps) => {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [loadingChat, setLoadingChat] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [suggestedReplies, setSuggestedReplies] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);

  const activeChatIdRef = useRef<number | null>(null);
  const lastAssistantMessageIdRef = useRef<number | null>(null);
  const isStreamingRef = useRef(false);
  const nextBeforeIdRef = useRef<number | null>(null);
  const subscriptionRef = useRef<ReturnType<
    typeof consumer.subscriptions.create
  > | null>(null);
  const streamTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const receivedChunkRef = useRef(false);

  // ActionCable's broadcast executor is multi-threaded server-side, so frames
  // from a single publisher can arrive on the socket out of order. The server
  // stamps stream events with `seq`; we apply them in order, buffering any
  // that arrive ahead of the gap. `expectedSeqRef` starts null and is seeded
  // by the first sequenced frame we receive — for a new chat the worker may
  // have broadcast earlier frames before the subscription opened, so we can
  // not assume the stream begins at seq=1.
  const expectedSeqRef = useRef<number | null>(null);
  const seqBufferRef = useRef<Map<number, StreamEvent>>(new Map());
  // Armed while the reorder buffer is non-empty (a gap is unresolved). If the
  // missing seq never arrives, `done` would sit buffered forever — this
  // surfaces an error and ends the stream.
  const gapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGapTimeout = useCallback(() => {
    if (gapTimeoutRef.current) {
      clearTimeout(gapTimeoutRef.current);
      gapTimeoutRef.current = null;
    }
  }, []);

  const resetSeqState = useCallback(() => {
    expectedSeqRef.current = null;
    seqBufferRef.current.clear();
    clearGapTimeout();
  }, [clearGapTimeout]);

  const clearStreamTimeout = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
    clearGapTimeout();
  }, [clearGapTimeout]);

  const armGapTimeout = useCallback(() => {
    gapTimeoutRef.current = setTimeout(() => {
      gapTimeoutRef.current = null;
      seqBufferRef.current.clear();
      setError(t('error_timeout'));
      setStreamingContent('');
      setIsStreaming(false);
      isStreamingRef.current = false;
    }, STREAM_TIMEOUT_MS);
  }, []);

  const subscribe = useCallback(
    (chatId: number) => {
      unsubscribe();
      resetSeqState();

      const processEvent = (data: StreamEvent) => {
        switch (data.type) {
          case 'chunk':
            receivedChunkRef.current = true;
            clearStreamTimeout();
            // Periodically the server attaches `full_content` — the authoritative
            // cumulative segment content. When present, replace whatever we
            // have accumulated locally so missed chunks can't cause drift.
            if (data.full_content !== undefined) {
              setStreamingContent(data.full_content);
            } else {
              setStreamingContent((prev) => prev + (data.content ?? ''));
            }
            break;
          case 'message_break':
            // Prefer the authoritative content from the server; fall back to
            // whatever streamed in case the server omitted it.
            setStreamingContent((prev) => {
              const committed = data.content ?? prev;
              if (committed) {
                setMessages((msgs) => [
                  ...msgs,
                  {
                    id: `assistant-${Date.now()}-${msgs.length}`,
                    role: 'assistant',
                    content: committed,
                  },
                ]);
              }
              return '';
            });
            break;
          case 'done':
            clearStreamTimeout();
            setStreamingContent((prev) => {
              if (prev) {
                setMessages((msgs) => [
                  ...msgs,
                  {
                    id: `assistant-${data.message_id ?? Date.now()}`,
                    role: 'assistant',
                    content: prev,
                  },
                ]);
              }
              return '';
            });
            setIsStreaming(false);
            isStreamingRef.current = false;
            lastAssistantMessageIdRef.current = data.message_id ?? null;
            break;
          case 'suggested_replies':
            if (data.message_id === lastAssistantMessageIdRef.current) {
              setSuggestedReplies(data.suggestions ?? []);
            }
            break;
          case 'title_updated':
            if (data.title) {
              const newTitle = data.title;
              setChats((prev) =>
                prev.map((c) =>
                  c.id === chatId ? { ...c, title: newTitle } : c
                )
              );
            }
            break;
          case 'error':
            clearStreamTimeout();
            setError(data.message ?? t('error_default'));
            setStreamingContent('');
            setIsStreaming(false);
            isStreamingRef.current = false;
            break;
        }
      };

      subscriptionRef.current = consumer.subscriptions.create(
        {
          channel: 'Agents::Channels::ChatChannel',
          chat_id: chatId,
        },
        {
          received(data: StreamEvent) {
            // Events without a seq (e.g. title_updated) bypass the reorder
            // buffer and apply immediately.
            if (data.seq === undefined) {
              processEvent(data);
              return;
            }

            // Seed the baseline from the first sequenced frame so we don't
            // deadlock waiting for seq=1 when early frames were broadcast
            // before the subscription opened.
            if (expectedSeqRef.current === null) {
              expectedSeqRef.current = data.seq;
            }

            if (data.seq < expectedSeqRef.current) {
              // Late or duplicate event from a prior stream; drop.
              return;
            }

            if (data.seq > expectedSeqRef.current) {
              seqBufferRef.current.set(data.seq, data);
              if (!gapTimeoutRef.current) {
                armGapTimeout();
              }
              return;
            }

            processEvent(data);
            expectedSeqRef.current += 1;
            while (seqBufferRef.current.has(expectedSeqRef.current)) {
              const next = seqBufferRef.current.get(expectedSeqRef.current);
              seqBufferRef.current.delete(expectedSeqRef.current);
              if (next) {
                processEvent(next);
              }
              expectedSeqRef.current += 1;
            }
            // Drain advanced; either we closed the gap (clear) or landed on a
            // new gap (restart from now).
            clearGapTimeout();
            if (seqBufferRef.current.size > 0) {
              armGapTimeout();
            }
          },
        }
      );
    },
    [
      armGapTimeout,
      clearGapTimeout,
      clearStreamTimeout,
      resetSeqState,
      unsubscribe,
    ]
  );

  const loadChat = useCallback(
    async (chatId: number) => {
      setLoadingChat(true);
      try {
        const response = await standardRequestWithErrorFlash.get<ChatDetail>(
          `/candidate_agent/chats/${chatId}`,
          { params: { application_id: applicationId } }
        );
        const detail = response.data;
        setMessages(detail.messages.map(toClientMessage));
        setStreamingContent('');
        setSuggestedReplies([]);
        lastAssistantMessageIdRef.current = null;
        setError(null);
        setActiveChatId(detail.id);
        activeChatIdRef.current = detail.id;
        setHasMoreMessages(detail.has_more);
        nextBeforeIdRef.current = detail.next_before_id;
        setLoadingEarlier(false);
        subscribe(detail.id);
      } catch {
        setError(t('error_load_failed'));
      } finally {
        setLoadingChat(false);
      }
    },
    [applicationId, subscribe]
  );

  const loadEarlierMessages = useCallback(async () => {
    const chatId = activeChatIdRef.current;
    const cursor = nextBeforeIdRef.current;
    if (
      chatId === null ||
      cursor === null ||
      !hasMoreMessages ||
      loadingEarlier ||
      isStreamingRef.current
    ) {
      return;
    }
    setLoadingEarlier(true);
    try {
      const response =
        await standardRequestWithErrorFlash.get<MessagePageResponse>(
          `/candidate_agent/chats/${chatId}/messages`,
          {
            params: {
              application_id: applicationId,
              before_id: cursor,
            },
          }
        );
      const page = response.data;
      if (activeChatIdRef.current !== chatId) {
        return; // user switched chats mid-request; discard stale page
      }
      const prepended = page.messages.map(toClientMessage);
      setMessages((prev) => [...prepended, ...prev]);
      nextBeforeIdRef.current = page.next_before_id;
      setHasMoreMessages(page.has_more);
    } catch {
      setError(t('error_load_failed'));
    } finally {
      setLoadingEarlier(false);
    }
  }, [applicationId, hasMoreMessages, loadingEarlier]);

  // Teardown only runs when the profile unmounts (page navigation/reload).
  useEffect(() => {
    return () => {
      unsubscribe();
      clearStreamTimeout();
    };
  }, [clearStreamTimeout, unsubscribe]);

  const startNewChat = useCallback(() => {
    if (isStreamingRef.current) {
      return;
    }
    unsubscribe();
    clearStreamTimeout();
    setMessages([]);
    setStreamingContent('');
    setSuggestedReplies([]);
    lastAssistantMessageIdRef.current = null;
    setError(null);
    setActiveChatId(null);
    activeChatIdRef.current = null;
    setHasMoreMessages(false);
    nextBeforeIdRef.current = null;
    setLoadingEarlier(false);
  }, [clearStreamTimeout, unsubscribe]);

  const refreshChats = useCallback(
    async (limit?: number) => {
      try {
        const response = await standardRequestWithErrorFlash.get<ChatSummary[]>(
          '/candidate_agent/chats',
          {
            params: {
              application_id: applicationId,
              limit: limit ?? undefined,
            },
          }
        );
        setChats(response.data);
      } catch {
        // Silent — the menu will show whatever we last fetched.
      }
    },
    [applicationId]
  );

  const renameChat = useCallback(
    async (id: number, title: string): Promise<boolean> => {
      try {
        const response = await standardRequestWithErrorFlash.patch<ChatSummary>(
          `/candidate_agent/chats/${id}`,
          { title, application_id: applicationId }
        );
        const updated = response.data;
        setChats((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, title: updated.title, updated_at: updated.updated_at }
              : c
          )
        );
        return true;
      } catch {
        return false;
      }
    },
    [applicationId]
  );

  const deleteChat = useCallback(
    async (id: number): Promise<boolean> => {
      try {
        await standardRequestWithErrorFlash.delete(
          `/candidate_agent/chats/${id}`,
          { params: { application_id: applicationId } }
        );
        setChats((prev) => prev.filter((c) => c.id !== id));
        if (activeChatIdRef.current === id) {
          unsubscribe();
          clearStreamTimeout();
          setMessages([]);
          setStreamingContent('');
          setSuggestedReplies([]);
          lastAssistantMessageIdRef.current = null;
          setError(null);
          setActiveChatId(null);
          activeChatIdRef.current = null;
          setIsStreaming(false);
          isStreamingRef.current = false;
          setHasMoreMessages(false);
          nextBeforeIdRef.current = null;
          setLoadingEarlier(false);
        }
        return true;
      } catch {
        return false;
      }
    },
    [applicationId, clearStreamTimeout, unsubscribe]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      isStreamingRef.current = true;
      setError(null);
      setStreamingContent('');
      setSuggestedReplies([]);
      lastAssistantMessageIdRef.current = null;
      receivedChunkRef.current = false;
      resetSeqState();

      const priorChatId = activeChatIdRef.current;

      try {
        const response = await standardRequestWithErrorFlash.post<{
          chat_id: number;
        }>('/candidate_agent/chats', {
          message: text,
          application_id: applicationId,
          chat_id: priorChatId,
        });

        const newChatId = response.data.chat_id;
        const isNewChat = priorChatId !== newChatId;

        if (isNewChat) {
          activeChatIdRef.current = newChatId;
          setActiveChatId(newChatId);
          setHasMoreMessages(false);
          nextBeforeIdRef.current = null;
          subscribe(newChatId);

          if (priorChatId === null) {
            setChats((prev) => {
              if (prev.some((c) => c.id === newChatId)) {
                return prev;
              }
              return [
                {
                  id: newChatId,
                  title: null,
                  updated_at: new Date().toISOString(),
                },
                ...prev,
              ];
            });
          }
        }

        streamTimeoutRef.current = setTimeout(() => {
          if (!receivedChunkRef.current) {
            setError(t('error_timeout'));
            setStreamingContent('');
            setIsStreaming(false);
            isStreamingRef.current = false;
          }
        }, STREAM_TIMEOUT_MS);
      } catch {
        setError(t('error_send_failed'));
        setIsStreaming(false);
        isStreamingRef.current = false;
      }
    },
    [applicationId, resetSeqState, subscribe]
  );

  return (
    <AgentChatContext.Provider
      value={{
        chats,
        activeChatId,
        loadingChat,
        messages,
        streamingContent,
        isStreaming,
        suggestedReplies,
        error,
        hasMoreMessages,
        loadingEarlier,
        sendMessage,
        loadChat,
        loadEarlierMessages,
        startNewChat,
        refreshChats,
        renameChat,
        deleteChat,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = (): AgentChatContextValue => {
  const ctx = useContext(AgentChatContext);
  if (!ctx) {
    throw new Error('useAgentChat must be used within AgentChatProvider');
  }
  return ctx;
};
